/* ME → BUSINESS — the basics (2026-10-13). Her business name, its web address
 * and what kind of business it is. The import-and-audit that will sit under
 * these is stored, not built (docs/me-business-import-spec.md).
 *
 * ONE STORED VALUE EACH, whichever screen edits it:
 *   - the name is `settings.business_name` (0028) — the same field Me → Brand
 *     shows beside her logos, where she put it on 2026-10-12. Two inputs, one
 *     value, so they cannot disagree.
 *   - the kind is `settings.audience` (0028), read by the Sites brief. It
 *     MOVED here from Settings; Settings now points at this screen.
 *   - the website is `settings.website` (0038), saved on its own like Skills,
 *     so a migration not yet run fails only the website, in amber.
 *   - the name she signs emails with is `settings.signoff` (0050), saved the
 *     same way. Read by every email's "Talk soon," (`personaliseEmail`).
 * The Accelerator reads the name and the website as facts (`aboutHerBlock`).
 *
 * Saves as she leaves a field (rule 1); no Save button.
 */
import { useEffect, useRef, useState } from "react";
import { Building2 } from "lucide-react";
import { SITE_AUDIENCES } from "../utils/siteBrief";

/* "tevrawood.com" → "https://tevrawood.com". Anything without a dot is not an
   address and is kept as typed, so she sees what she wrote rather than a
   rewrite she did not ask for. */
export function normWebsite(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return /\.[a-z]{2,}/i.test(s) && !/\s/.test(s) ? `https://${s}` : s;
}

const card = { background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "18px 18px 20px" };
const label = { display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 5 };
const field = {
  width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8,
  border: "1px solid var(--border)", background: "var(--bg-primary)", color: "var(--text-primary)",
  fontSize: 15, fontFamily: "inherit",
};

export function BusinessPanel({ userSettings = {}, saveSettings, onSaveWebsite, onSaveSignoff, isMobile }) {
  const [name, setName] = useState(userSettings.businessName || "");
  const [site, setSite] = useState(userSettings.website || "");
  const [signoff, setSignoff] = useState(userSettings.signoff || "");
  const [status, setStatus] = useState("idle"); // idle | saving | saved | failed
  const [failed, setFailed] = useState("");     // which field did not save
  const touched = useRef(false);
  useEffect(() => { if (!touched.current) setName(userSettings.businessName || ""); }, [userSettings.businessName]);
  useEffect(() => { if (!touched.current) setSite(userSettings.website || ""); }, [userSettings.website]);
  useEffect(() => { if (!touched.current) setSignoff(userSettings.signoff || ""); }, [userSettings.signoff]);

  const audience = userSettings.audience || "general-coach";

  /* Name and kind go through saveSettings (0028, run 2026-09-22), which does
     not report failure — the same optimistic tick Me → Brand gives them. */
  const saveField = (key, value) => {
    touched.current = true;
    if ((userSettings[key] || "") === value) return;
    saveSettings({ [key]: value });
    setStatus("saved");
  };
  const saveSite = async () => {
    touched.current = true;
    const next = normWebsite(site);
    setSite(next);
    if ((userSettings.website || "") === next) return;
    setStatus("saving");
    const ok = await onSaveWebsite(next);
    setFailed(ok ? "" : "Your website");
    setStatus(ok ? "saved" : "failed");
  };
  /* The name her emails are signed with (supabase/0050), saved on its own
     like the website, so a column not yet there fails only this, in amber. */
  const saveSignoff = async () => {
    touched.current = true;
    const next = signoff.trim();
    setSignoff(next);
    if ((userSettings.signoff || "") === next) return;
    setStatus("saving");
    const ok = await onSaveSignoff(next);
    setFailed(ok ? "" : "The name you sign with");
    setStatus(ok ? "saved" : "failed");
  };

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", background: "var(--bg-primary)", padding: isMobile ? "16px 16px 100px" : "32px 32px 48px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Building2 size={18} color="var(--accent)" />
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--text-primary)" }}>Business</h2>
            <p role="status" style={{ margin: "2px 0 0", fontSize: 12.5, color: status === "failed" ? "var(--warn-text)" : "var(--text-secondary)" }}>
              {status === "failed" ? `${failed || "That"} didn't save. Check your connection and try again.`
                : status === "saving" ? "Saving…"
                : status === "saved" ? "✓ Saved"
                : "The basics. Brill knows these before your first module."}
            </p>
          </div>
        </div>

        <div style={card}>
          <label style={label} htmlFor="business-name">Business name</label>
          <input id="business-name" value={name}
            onChange={(e) => { touched.current = true; setName(e.target.value); }}
            onBlur={() => saveField("businessName", name.trim())}
            placeholder="What your business is called" style={{ ...field, marginBottom: 16 }} />

          <label style={label} htmlFor="business-website">Website</label>
          <input id="business-website" type="url" inputMode="url" value={site}
            onChange={(e) => { touched.current = true; setSite(e.target.value); }}
            onBlur={saveSite}
            placeholder="yourbusiness.com" style={{ ...field, marginBottom: 16 }} />

          {/* HOW HER EMAILS ARE SIGNED (her ask, 2026-11-02): "Talk soon,"
              and this, under every newsletter and Outreach email. Empty, the
              name Brill calls her stands in — shown as the placeholder. */}
          <label style={label} htmlFor="business-signoff">Sign your emails as</label>
          <input id="business-signoff" value={signoff}
            onChange={(e) => { touched.current = true; setSignoff(e.target.value); }}
            onBlur={saveSignoff}
            placeholder={userSettings.displayName || "Your first name"} style={{ ...field, marginBottom: 6 }} />
          <p style={{ margin: "0 0 16px", fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.6 }}>
            Your emails end "Talk soon," and this name.
          </p>

          {/* WHAT KIND OF BUSINESS THIS IS — moved from Settings 2026-10-13.
              A site builder leans the whole page on this, so it is asked once
              and the honest default is the neutral one. */}
          <label style={label} htmlFor="business-kind">What kind of business is this?</label>
          <select id="business-kind" value={audience}
            onChange={(e) => saveField("audience", e.target.value)}
            style={{ ...field, marginBottom: 6 }}>
            {SITE_AUDIENCES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
          <p style={{ margin: 0, fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.6 }}>
            {(SITE_AUDIENCES.find((a) => a.id === audience) || {}).hint}
          </p>
        </div>
      </div>
    </div>
  );
}
